// src/components/desk/LeadAlertToggle.jsx — "tell me on Telegram when a new lead matches this client"
// (owner 2026-09-16; scheduler 0018).
//
// Lives beside the client modal's „მოძებნე ლიდებში“ button and is built from the SAME `committed`
// search that button opens, so the alert watches exactly the list the button shows. What is stored
// is the leads-search QUERY STRING (applySearchFilterParams, the function getAgentListings uses);
// the server notifier replays it as this agent. If the client's criteria are edited, the next time
// the modal renders the stored string is refreshed (and the server re-seeds, so leads that merely
// start matching because the search widened are not announced as arrivals).
//
// ⚠️ Rendered only where the criteria are visible (the hatch is absent for a masked client), but that
// is not the gate: the notifier re-checks access with the client modal's own rule every cycle.
import { useEffect, useMemo, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { intentLabel } from '../../lib/labels.js'
import { requirementToCommitted, committedToAdvFilters } from '../../lib/deskSearchState.js'
import { leadWatchGaps } from '../../lib/leadWatchRules.js'
import { applySearchFilterParams } from '../../db/core.js'
import { getClientLeadWatch, clientLeadWatchSet, clientLeadWatchDelete, clientLeadWatchMute, getMyNotifyState } from '../../db/index.js'
import { Icon } from '../Icon.jsx'

// One read per session is enough to say "connect Telegram first"; the Settings tab is the truth.
let _notifyStatePromise = null
const notifyState = () => (_notifyStatePromise ||= getMyNotifyState().catch(() => null))

export function leadWatchSummary(filters = {}) {
  const f = filters || {}
  const parts = []
  if (f.intents?.length === 1) parts.push(intentLabel(f.intents[0], 'demand'))
  if (f.roomsSet?.length) parts.push(`${f.roomsSet.length > 1 ? `${f.roomsSet[0]}–${f.roomsSet[f.roomsSet.length - 1]}` : f.roomsSet[0]} ${t('lead_watch_rooms')}`)
  const range = (lo, hi, unit) => `${lo && hi ? `${lo}–${hi}` : hi ? `≤ ${hi}` : `≥ ${lo}`} ${unit}`.trim()
  if (f.priceMin || f.priceMax) parts.push(range(f.priceMin, f.priceMax, f.currency || ''))
  if (f.areaMin || f.areaMax) parts.push(range(f.areaMin, f.areaMax, 'm²'))
  if (f.districtCodes?.length) parts.push(tLog('lead_watch_districts', { n: String(f.districtCodes.length) }))
  return parts.join(' · ')
}

// The client card's entry point (owner 2026-09-17: "how does an agent select notify here?" — it had
// been placed at the bottom of the matches list, below everything). Builds the SAME search the
// „მოძებნე ლიდებში" button opens, from the client's own criteria.
// ⚠️ THE GATE (owner 2026-09-21: "if the client can't match any lead because of missing information,
// tell the agent to fill those before activating"). leadWatchGaps — the same function the notifier
// runs before every search — lists what is missing; while anything is, the chip does not subscribe,
// it opens the editor (`onFill`) and names the fields. Measured the same day: 119 of 178 open clients
// lacked budget, district or rooms/area, and 78 had type "other" — before this, "rent" alone was a
// watch over 13,760 leads.
export function ClientLeadAlert({ requirementId, clientName, criteria, intent, propertyCategory = null, onFill = null, compact = false }) {
  const committed = useMemo(() => requirementToCommitted(criteria || {}, intent, requirementId, propertyCategory), [criteria, intent, requirementId, propertyCategory])
  const filters = useMemo(() => committedToAdvFilters(committed, {}), [committed])
  const gaps = useMemo(() => leadWatchGaps(criteria || {}, { intent, property_category: propertyCategory }), [criteria, intent, propertyCategory])
  return <LeadAlertToggle requirementId={requirementId} clientName={clientName} committed={committed} filters={filters} compact={compact}
    gaps={gaps} onFill={onFill} />
}

export const leadWatchGapList = gaps => (gaps || []).map(g => t(`lead_watch_gap_${g}`)).join(', ')

// `compact` (client modal redesign 1b): a 28px pill in the identity line — „ლიდები ჩართულია" — with the
// connect-Telegram / lost-access hints moved into its tooltip and a small amber dot.
export function LeadAlertToggle({ requirementId, clientName, committed, filters, compact = false, gaps = [], onFill = null }) {
  const searchQs = useMemo(() => {
    const q = new URLSearchParams()
    applySearchFilterParams(q, filters, { scope: 'leads' })
    return q.toString()
  }, [filters])
  const summary = useMemo(() => leadWatchSummary(committed?.filters), [committed])
  const [watch, setWatch] = useState(undefined)   // undefined = loading, null = not watching
  const [linked, setLinked] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!requirementId) return
    let live = true
    getClientLeadWatch(requirementId).then(async w => {
      if (!live) return
      // The criteria changed since the alert was turned on: keep watching the list the button shows NOW.
      if (w && searchQs && w.search_qs !== searchQs) {
        try { w = await clientLeadWatchSet({ requirementId, clientName, summary, searchQs }) } catch { /* keep the old one */ }
      }
      if (live) setWatch(w || null)
    }).catch(() => { if (live) setWatch(null) })
    notifyState().then(s => { if (live) setLinked(s ? !!s.linked : null) })
    return () => { live = false }
  }, [requirementId, searchQs])   // eslint-disable-line react-hooks/exhaustive-deps

  if (!requirementId || watch === undefined) return null
  const blocked = gaps.length > 0
  // Too vague to watch and not already watched: the chip is the instruction, not a switch.
  if (blocked && !(watch && !watch.muted)) {
    const title = tLog('lead_watch_fill_title', { list: leadWatchGapList(gaps) })
    return (
      <button type="button" disabled={!onFill} onClick={() => onFill?.()} data-lead-alert="needs-fill" title={title}
        className={`inline-flex items-center gap-1.5 rounded-full border border-dashed border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 disabled:opacity-60 ${compact
          ? 'h-7 px-2.5 text-xs whitespace-nowrap' : 'text-sm px-3 py-1.5'}`}>
        <Icon name="bell" size={compact ? 12 : 13} />
        {t('lead_chip_fill')}
      </button>
    )
  }
  if (!searchQs) return null

  async function run(fn) {
    setBusy(true); setErr('')
    try { await fn() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const on = !!watch && !watch.muted
  const onClick = () => run(async () => {
    if (on) { await clientLeadWatchDelete(watch.id); setWatch(null) }
    else if (watch?.muted) { await clientLeadWatchMute(watch.id, false); setWatch({ ...watch, muted: false }) }
    else setWatch(await clientLeadWatchSet({ requirementId, clientName, summary, searchQs }))
  })
  if (compact) {
    const warn = blocked ? tLog('lead_watch_paused_gaps', { list: leadWatchGapList(gaps) })
      : watch && linked === false ? t('lead_watch_connect_first') : watch?.access_ok === false ? t('lead_watch_no_access') : err || null
    return (
      <button type="button" disabled={busy} onClick={onClick} data-lead-alert={watch ? (watch.muted ? 'muted' : 'on') : 'off'}
        title={[on ? t('lead_watch_on_title') : t('lead_watch_off_title'), warn].filter(Boolean).join(' — ')}
        className={`relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs whitespace-nowrap disabled:opacity-50 ${on
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'border-border text-muted-foreground hover:text-foreground'}`}>
        <Icon name="bell" size={12} />
        {on ? t('lead_chip_on') : watch?.muted ? t('lead_chip_muted') : t('lead_chip_off')}
        {warn && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500" aria-hidden />}
      </button>
    )
  }
  return (
    <span className="inline-flex flex-col gap-0.5" data-lead-alert={watch ? (watch.muted ? 'muted' : 'on') : 'off'}>
      <button type="button" disabled={busy}
        title={on ? t('lead_watch_on_title') : t('lead_watch_off_title')}
        className={`text-sm px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 disabled:opacity-50 ${on
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          : 'border-border hover:bg-accent'}`}
        onClick={() => run(async () => {
          if (on) { await clientLeadWatchDelete(watch.id); setWatch(null) }
          else if (watch?.muted) { await clientLeadWatchMute(watch.id, false); setWatch({ ...watch, muted: false }) }
          else setWatch(await clientLeadWatchSet({ requirementId, clientName, summary, searchQs }))
        })}>
        <Icon name="bell" size={13} />
        {on ? t('lead_watch_on') : watch?.muted ? t('lead_watch_muted') : t('lead_watch_off')}
      </button>
      {watch && linked === false && <span className="text-[11px] text-amber-700 dark:text-amber-400">{t('lead_watch_connect_first')}</span>}
      {watch?.access_ok === false && <span className="text-[11px] text-red-600 dark:text-red-400">{t('lead_watch_no_access')}</span>}
      {blocked && <span className="text-[11px] text-amber-700 dark:text-amber-400">{tLog('lead_watch_paused_gaps', { list: leadWatchGapList(gaps) })}</span>}
      {err && <span className="text-[11px] text-destructive">{err}</span>}
    </span>
  )
}
